/**
 * Typed access to all VITE_-prefixed environment variables.
 *
 * Import from here — never read import.meta.env directly in application code.
 * The parallel threads (design-system, typed-client, i18n) and all feature
 * slices should import their env values from this module.
 */

/** Base URL of the backend API (e.g. "http://localhost:8000") */
export const API_URL: string = import.meta.env.VITE_API_URL ?? '';

// ---------------------------------------------------------------------------
// Module enable flags — mirror the backend env-driven module registry.
// Each VITE_MODULE_* env var defaults to "false" if absent so the build is
// safe with a minimal .env file.
// ---------------------------------------------------------------------------

function boolEnv(key: string, defaultValue = false): boolean {
  const raw = import.meta.env[key];
  if (raw === undefined || raw === '') return defaultValue;
  return raw === 'true' || raw === '1';
}

export const MODULE_PING = boolEnv('VITE_MODULE_PING', false); // off — internal health page, not part of the customer portal
export const MODULE_LIFTS = boolEnv('VITE_MODULE_LIFTS', true); // on by default — core customer portal feature
export const MODULE_ORGANIZATIONS = boolEnv('VITE_MODULE_ORGANIZATIONS');
export const MODULE_RBAC = boolEnv('VITE_MODULE_RBAC');
export const MODULE_INVITATIONS = boolEnv('VITE_MODULE_INVITATIONS');
export const MODULE_MFA = boolEnv('VITE_MODULE_MFA');
export const MODULE_AUDIT = boolEnv('VITE_MODULE_AUDIT');
export const MODULE_BILLING = boolEnv('VITE_MODULE_BILLING');
export const MODULE_DOCUMENTS = boolEnv('VITE_MODULE_DOCUMENTS', true); // on by default — core customer portal feature

// ---------------------------------------------------------------------------
// Language enable flags — control which language packs are bundled.
// VITE_LANG_EN defaults to true (always on); VITE_LANG_DE defaults to true.
// Set VITE_LANG_DE=false to ship an EN-only build with no language switcher.
// ---------------------------------------------------------------------------

export const LANG_EN = boolEnv('VITE_LANG_EN', true);
export const LANG_DE = boolEnv('VITE_LANG_DE', true);

// ---------------------------------------------------------------------------
// Footer legal/contact links — each renders only when its env var is set.
// Absent/empty = that link is omitted; the AppFooter widget renders nothing
// at all when none of the three are configured.
// ---------------------------------------------------------------------------

export const IMPRESSUM_URL: string | undefined = import.meta.env.VITE_IMPRESSUM_URL || undefined;
export const PRIVACY_URL: string | undefined = import.meta.env.VITE_PRIVACY_URL || undefined;
export const SUPPORT_EMAIL: string | undefined = import.meta.env.VITE_SUPPORT_EMAIL || undefined;

export const env = {
  API_URL,
  MODULE_PING,
  MODULE_LIFTS,
  MODULE_ORGANIZATIONS,
  MODULE_RBAC,
  MODULE_INVITATIONS,
  MODULE_MFA,
  MODULE_AUDIT,
  MODULE_BILLING,
  MODULE_DOCUMENTS,
  LANG_EN,
  LANG_DE,
  IMPRESSUM_URL,
  PRIVACY_URL,
  SUPPORT_EMAIL,
} as const;
